import api from './apiService';

/**
 * Service for website-related operations
 */
const websiteService = {
  /**
   * Get all websites owned by the current user
   * @returns {Promise} - Response with websites
   */
  getUserWebsites: async () => {
    try {
      const response = await api.get('/websites');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get websites');
    }
  },

  /**
   * Get all websites (for admin)
   * @returns {Promise} - Response with all websites
   */
  getAllWebsites: async () => {
    try {
      const response = await api.get('/websites/all');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get all websites');
    }
  },

  /**
   * Get website by ID
   * @param {string} id - Website ID
   * @returns {Promise} - Response with website details
   */
  getWebsiteById: async (id) => {
    try {
      const response = await api.get(`/websites/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get website');
    }
  },
  
  /**
   * Get website by URL
   * @param {string} url - Website URL
   * @returns {Promise} - Response with website details if found
   */
  getWebsiteByUrl: async (url) => {
    try {
      const response = await api.get(`/websites/by-url`, { params: { url } });
      return response.data;
    } catch (error) {
      throw error; // Preserve original error for 404 checks
    }
  },
  
  /**
   * Create a new website
   * @param {Object} websiteData - Website data
   * @returns {Promise} - Response with created website
   */
  createWebsite: async (websiteData) => {
    try {
      const response = await api.post('/websites', websiteData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create website');
    }
  },
  
  /**
   * Update a website
   * @param {string} id - Website ID
   * @param {Object} websiteData - Updated website data
   * @returns {Promise} - Response with updated website
   */
  updateWebsite: async (id, websiteData) => {
    try {
      const response = await api.put(`/websites/${id}`, websiteData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update website');
    }
  },
  
  /**
   * Delete a website
   * @param {string} id - Website ID
   * @returns {Promise} - Response with deletion status
   */
  deleteWebsite: async (id) => {
    try {
      const response = await api.delete(`/websites/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete website');
    }
  },
  
  /**
   * Get all pages for a website
   * @param {string} id - Website ID
   * @returns {Promise} - Response with website pages
   */
  getWebsitePages: async (id) => {
    try {
      const response = await api.get(`/websites/${id}/pages`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get website pages');
    }
  },

  /**
   * Recalculate website health score
   * @param {string} id - Website ID
   * @returns {Promise} - Response with updated health score
   */
  recalculateHealthScore: async (id) => {
    try {
      const response = await api.post(`/websites/${id}/recalculate`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to recalculate health score');
    }
  },

  /**
   * Crawl a website to discover pages
   * @param {string} id - Website ID
   * @param {Object} options - Crawl options
   * @param {number} options.maxPages - Maximum number of pages to discover (default: 200)
   * @param {boolean} options.includeExternal - Whether to include external links (default: false)
   * @param {boolean} options.createPages - Whether to create pages in the database (default: true)
   * @returns {Promise} - Response with crawling status
   */
  crawlWebsite: async (id, options = {}) => {
    try {
      const response = await api.post(`/websites/${id}/crawl`, options);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to crawl website');
    }
  },

  /**
   * Get the current crawling status for a website
   * @param {string} id - Website ID
   * @returns {Promise} - Response with crawling status
   */
  getCrawlingStatus: async (id) => {
    try {
      const response = await api.get(`/websites/${id}/crawling-status`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get crawling status');
    }
  },
  
  /**
   * Wait for website crawling to complete
   * @param {string} id - Website ID
   * @param {number} timeoutMs - Timeout in milliseconds
   * @returns {Promise} - Response with processed website
   */
  waitForCrawlingComplete: async (id, timeoutMs = 30000) => {
    try {
      const startTime = Date.now();
      let website = null;
      let status = null;
      
      // Poll until crawling is complete or timeout
      while (Date.now() - startTime < timeoutMs) {
        try {
          // Check crawling status first
          const statusResponse = await websiteService.getCrawlingStatus(id);
          status = statusResponse.status || 'unknown';
          
          // If status is completed or error, get the website
          if (status === 'completed' || status === 'error' || status === 'idle') {
            const websiteResponse = await websiteService.getWebsiteById(id);
            website = websiteResponse.website || websiteResponse;
            
            if (website.status === 'active' || website.crawl_status === 'completed') {
              return { website, status };
            }
          }
        } catch (pollingError) {
          console.error("Error while polling website crawling status:", pollingError);
        }
        
        // Wait before checking again
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
      
      // If we reach here, timed out - try to get the latest website data
      try {
        const response = await websiteService.getWebsiteById(id);
        website = response.website || response;
      } catch (finalError) {
        console.error("Failed to get final website data after timeout:", finalError);
      }
      
      // Return with timeout flag
      return { website, status, timedOut: true };
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to wait for website crawling');
    }
  }
};

export default websiteService;